import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { feedbackApi, FEEDBACK_MEDIA_BASE } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MultiSelect } from '../components/MultiSelect';
import { Checkbox } from '../components/ui/checkbox';
import { Loader2, RefreshCw, Trash2, MessageSquare, ExternalLink, RotateCw, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const SETTABLE_STATUSES = ['Open', 'In Progress', 'In QC', 'Not Possible', 'Closed'];

const STATUS_STYLES = {
  'Open': 'bg-blue-100 text-blue-700',
  'In Progress': 'bg-amber-100 text-amber-700',
  'In Development': 'bg-amber-100 text-amber-700',
  'In QC': 'bg-purple-100 text-purple-700',
  'Not Possible': 'bg-gray-200 text-gray-600',
  'Closed': 'bg-green-100 text-green-700',
};
const statusClass = (s) => STATUS_STYLES[s] || 'bg-[#ECEDF5] text-[#3E3F96]';

const fmtDateTime = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (isNaN(d)) return '-';
  return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// Derive a readable "screen" label from the recorded URL.
const screenOf = (url) => {
  if (!url) return 'General';
  try {
    const p = new URL(url).pathname;
    const seg = p.split('/').filter(Boolean)[0] || 'dashboard';
    return seg.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  } catch (_) {
    return 'General';
  }
};

const mediaUrl = (p) => (!p ? null : /^https?:\/\//.test(p) ? p : `${FEEDBACK_MEDIA_BASE}${p}`);

const FeedbackTicketsPage = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState([]);
  const [screenFilter, setScreenFilter] = useState([]);

  const [active, setActive] = useState(null); // currently opened ticket (full doc)
  const [syncing, setSyncing] = useState(false);

  const fetchTickets = useCallback(async (quiet) => {
    if (!quiet) setLoading(true);
    try {
      const res = await feedbackApi.list();
      setTickets(res.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load feedback tickets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  // While a ticket whose transcript is still generating is open, refresh it.
  useEffect(() => {
    if (!active) return;
    if (!['pending', 'running'].includes(active.transcript_status)) return;
    const t = setInterval(async () => {
      try {
        const res = await feedbackApi.get(active.id);
        setActive((cur) => (cur && cur.id === res.data.id ? res.data : cur));
        if (!['pending', 'running'].includes(res.data.transcript_status)) {
          fetchTickets(true);
        }
      } catch (_) {}
    }, 5000);
    return () => clearInterval(t);
  }, [active, fetchTickets]);

  const screens = useMemo(() => {
    const set = new Set(tickets.map((t) => screenOf(t.url)));
    return Array.from(set).sort();
  }, [tickets]);

  const statuses = useMemo(() => {
    const set = new Set([...SETTABLE_STATUSES, ...tickets.map((t) => t.status).filter(Boolean)]);
    return Array.from(set);
  }, [tickets]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tickets.filter((t) => {
      if (statusFilter.length && !statusFilter.includes(t.status)) return false;
      if (screenFilter.length && !screenFilter.includes(screenOf(t.url))) return false;
      if (q) {
        const hay = `${t.ticket_no} ${t.pms_ticket_no} ${t.url} ${t.created_by} ${t.transcript}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [tickets, search, statusFilter, screenFilter]);

  const openTicket = async (t) => {
    setActive(t); // optimistic
    try {
      const res = await feedbackApi.get(t.id, 1); // pull latest from PMS on open
      setActive(res.data);
      fetchTickets(true);
    } catch (_) {}
  };

  const changeStatus = async (status) => {
    if (!active) return;
    try {
      await feedbackApi.setStatus(active.id, { status });
      const res = await feedbackApi.get(active.id);
      setActive(res.data);
      fetchTickets(true);
      toast.success(`Status set to ${status}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to set status');
    }
  };

  const toggleCheck = async (idx, done) => {
    if (!active) return;
    const next = { ...active, changes: active.changes.map((c, i) => (i === idx ? { ...c, done } : c)) };
    setActive(next);
    try {
      await feedbackApi.setCheck(active.id, { index: idx, done });
    } catch (err) {
      toast.error('Failed to update checklist');
      setActive(active); // revert
    }
  };

  const syncNow = async () => {
    if (!active) return;
    setSyncing(true);
    try {
      const res = await feedbackApi.sync(active.id);
      setActive(res.data);
      fetchTickets(true);
      toast.success('Synced with PMS');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const retry = async () => {
    if (!active) return;
    try {
      await feedbackApi.retranscribe(active.id);
      const res = await feedbackApi.get(active.id);
      setActive(res.data);
      toast.success('Re-generating transcript & checklist');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start re-generation');
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete ticket ${t.ticket_no}? This removes its video/audio permanently.`)) return;
    try {
      await feedbackApi.remove(t.id);
      toast.success('Ticket deleted');
      if (active && active.id === t.id) setActive(null);
      fetchTickets(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1C1F1D] flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-[#3E3F96]" /> Feedback Tickets
          </h1>
          <p className="text-sm text-[#5C635F] mt-1">Screen-recorded feedback turned into actionable, trackable tickets.</p>
        </div>
        <Button variant="outline" onClick={() => fetchTickets()} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3">
            <Input
              placeholder="Search ticket #, screen, user, transcript…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-xs"
              data-testid="feedback-search"
            />
            <MultiSelect widthClass="w-44" placeholder="All statuses"
              options={statuses} selected={statusFilter} onChange={setStatusFilter} />
            <MultiSelect widthClass="w-44" placeholder="All screens"
              options={screens} selected={screenFilter} onChange={setScreenFilter} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#3E3F96]" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-[#5C635F]">
              <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>No feedback tickets yet.</p>
              <p className="text-xs mt-1">Use the <b>Feedback</b> button (bottom-right) to record one.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs uppercase font-semibold">Ticket</TableHead>
                  <TableHead className="text-xs uppercase font-semibold">Screen</TableHead>
                  <TableHead className="text-xs uppercase font-semibold">Raised by</TableHead>
                  <TableHead className="text-xs uppercase font-semibold">Created</TableHead>
                  <TableHead className="text-xs uppercase font-semibold">Checklist</TableHead>
                  <TableHead className="text-xs uppercase font-semibold">Status</TableHead>
                  <TableHead className="text-xs uppercase font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((t) => {
                  const done = (t.changes || []).filter((c) => c.done).length;
                  const total = (t.changes || []).length;
                  return (
                    <TableRow key={t.id} className="cursor-pointer hover:bg-[#F5F5FA]" onClick={() => openTicket(t)}>
                      <TableCell className="font-medium text-[#3E3F96]">
                        {t.ticket_no}
                        {t.pms_ticket_no && t.pms_ticket_no !== t.ticket_no && (
                          <span className="block text-[10px] text-[#8A8E8B]">PMS {t.pms_ticket_no}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-[#5C635F]">{screenOf(t.url)}</TableCell>
                      <TableCell className="text-[#5C635F]">{t.created_by || '—'}</TableCell>
                      <TableCell className="text-[#5C635F] text-sm">{fmtDateTime(t.created_at)}</TableCell>
                      <TableCell className="text-[#5C635F] text-sm">
                        {['pending', 'running'].includes(t.transcript_status)
                          ? <span className="text-amber-600">generating…</span>
                          : total ? `${done}/${total}` : '—'}
                      </TableCell>
                      <TableCell><Badge className={statusClass(t.status)}>{t.status}</Badge></TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="text-[#C85A40]" onClick={() => remove(t)} title="Delete">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {active.ticket_no}
                  <Badge className={statusClass(active.status)}>{active.status}</Badge>
                  {active.url && (
                    <a href={active.url} target="_blank" rel="noreferrer" className="text-xs text-[#3E3F96] inline-flex items-center gap-1 font-normal">
                      open screen <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </DialogTitle>
              </DialogHeader>

              <div className="grid md:grid-cols-3 gap-5">
                {/* Left: content */}
                <div className="md:col-span-2 space-y-4">
                  {mediaUrl(active.video_url) && (
                    <video src={mediaUrl(active.video_url)} controls className="w-full rounded-lg border border-[#D8D9E8] bg-black" />
                  )}
                  {!active.video_url && mediaUrl(active.audio_url) && (
                    <audio src={mediaUrl(active.audio_url)} controls className="w-full" />
                  )}

                  {/* AI checklist */}
                  <div>
                    <p className="text-sm font-semibold text-[#1C1F1D] mb-2">Action checklist</p>
                    {['pending', 'running'].includes(active.transcript_status) ? (
                      <div className="flex items-center gap-2 text-sm text-amber-600">
                        <Loader2 className="w-4 h-4 animate-spin" /> Generating from the recording…
                      </div>
                    ) : active.transcript_status === 'failed' ? (
                      <div className="flex items-center justify-between gap-2 text-sm text-[#C85A40] bg-[#C85A40]/10 rounded p-2">
                        <span className="flex items-center gap-2"><AlertCircle className="w-4 h-4" /> {active.transcribe_error || 'AI generation failed.'}</span>
                        <Button size="sm" variant="outline" onClick={retry}><RotateCw className="w-3 h-3 mr-1" /> Retry</Button>
                      </div>
                    ) : (active.changes || []).length === 0 ? (
                      <p className="text-sm text-[#8A8E8B]">No checklist items{active.transcript ? '' : ' (AI disabled — add OPENAI_API_KEY to enable).'}.</p>
                    ) : (
                      <ul className="space-y-2">
                        {active.changes.map((c, i) => (
                          <li key={i} className="flex gap-2 items-start">
                            <Checkbox checked={!!c.done} onCheckedChange={(v) => toggleCheck(i, !!v)} className="mt-0.5" />
                            <div>
                              <p className={`text-sm font-medium ${c.done ? 'line-through text-[#8A8E8B]' : 'text-[#1C1F1D]'}`}>{c.item}</p>
                              {c.detail && <p className="text-xs text-[#5C635F]">{c.detail}</p>}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* Transcript */}
                  {active.transcript && (
                    <details className="text-sm">
                      <summary className="cursor-pointer font-semibold text-[#1C1F1D]">Transcript</summary>
                      <p className="mt-2 text-[#5C635F] whitespace-pre-wrap max-h-48 overflow-y-auto">{active.transcript}</p>
                    </details>
                  )}

                  {/* Trail */}
                  {(active.trail || []).length > 0 && (
                    <div className="text-xs text-[#5C635F]">
                      <span className="font-semibold">Screens visited: </span>{active.trail.join(' → ')}
                    </div>
                  )}
                </div>

                {/* Right: action + history */}
                <div className="space-y-4">
                  <div>
                    <p className="text-xs font-semibold text-[#5C635F] uppercase tracking-wider mb-2">Set status</p>
                    <Select value={active.status} onValueChange={changeStatus}>
                      <SelectTrigger data-testid="feedback-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {SETTABLE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {active.pms_ticket_no && (
                      <p className="text-[11px] text-[#8A8E8B] mt-1">PMS may overwrite this on the next sync.</p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Button variant="outline" className="w-full" onClick={syncNow} disabled={syncing}>
                      {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                      {active.pms_ticket_no ? 'Sync now' : 'Push to PMS'}
                    </Button>
                    {active.pms_ticket_no && <p className="text-[11px] text-[#8A8E8B]">PMS #{active.pms_ticket_no} · synced {fmtDateTime(active.pms_synced_at)}</p>}
                    {active.pms_error && <p className="text-[11px] text-[#C85A40]">PMS: {active.pms_error}</p>}
                  </div>

                  <div>
                    <p className="text-xs font-semibold text-[#5C635F] uppercase tracking-wider mb-2">History</p>
                    {(active.history || []).length === 0 ? (
                      <p className="text-xs text-[#8A8E8B]">No status changes yet.</p>
                    ) : (
                      <ol className="space-y-2">
                        {active.history.map((h, i) => (
                          <li key={i} className="text-xs border-l-2 border-[#D8D9E8] pl-2">
                            <Badge className={`${statusClass(h.status)} text-[10px]`}>{h.status}</Badge>
                            <span className="block text-[#8A8E8B] mt-0.5">{fmtDateTime(h.at)}{h.by ? ` · ${h.by}` : ''}</span>
                            {h.remarks && <span className="block text-[#5C635F]">{h.remarks}</span>}
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>

                  <div className="text-[11px] text-[#8A8E8B] pt-2 border-t border-[#D8D9E8]">
                    Raised by {active.created_by || '—'}<br />{fmtDateTime(active.created_at)}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FeedbackTicketsPage;
